import { parseOrderTrackingSourceOrderId } from "@redotech/order-tracking-common/source-order-id";
import { TrackingPageOrder } from "@redotech/order-tracking-common/tracking-page-order-definition";

/**
 * Normalized customer/order attributes shared by all offer network providers;
 * per-provider mappers convert onto each vendor's all-string vocabulary.
 */
export interface OfferNetworkAttributes {
  email: string;
  firstname?: string;
  lastname?: string;
  /** Display order reference, e.g. "#1042". */
  orderRef?: string;
  /** Platform order id, e.g. Shopify's "7428562223265" (never the gid). */
  platformOrderId?: string;
  mobile?: string;
  /** ISO 3166-1 alpha-2, uppercase. */
  country?: string;
  shippingzipcode?: string;
  /** ISO 639-1. */
  language?: string;
  /** ISO 4217. */
  currency?: string;
  /** Line-item subtotal (excludes shipping/tax), fixed to 2 decimals. */
  amount?: string;
  cartItems?: { name: string; quantity: number; price?: string }[];
}

/**
 * Returns undefined when there's no email — providers require it. `overrideEmail`
 * replaces the customer's email (builder previews send a Redo address instead).
 */
export function buildOfferAttributes(
  order: TrackingPageOrder,
  locale: string | undefined,
  overrideEmail?: string,
): OfferNetworkAttributes | undefined {
  const customer = order.customer;
  const email = overrideEmail ?? customer?.email?.trim();
  if (!email) {
    return undefined;
  }

  let firstname = customer?.firstName ?? undefined;
  let lastname = customer?.lastName ?? undefined;
  if (!firstname && !lastname && customer?.name) {
    const [first, ...rest] = customer.name.trim().split(/\s+/);
    firstname = first || undefined;
    lastname = rest.length ? rest.join(" ") : undefined;
  }

  const countryCode = order.shippingAddress?.countryCode?.trim();
  const country =
    countryCode?.length === 2 ? countryCode.toUpperCase() : undefined;

  const language = locale?.split("-")[0]?.toLowerCase() || undefined;

  let amountTotal = 0;
  let hasPricedItem = false;
  for (const item of order.lineItems) {
    const price = item.discountedPrice ?? item.undiscountedPrice;
    if (price) {
      hasPricedItem = true;
      amountTotal += price.amount * item.quantity;
    }
  }

  const cartItems = order.lineItems
    .filter((item) => item.name)
    .map((item) => {
      const price = item.discountedPrice ?? item.undiscountedPrice;
      return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        name: item.name!,
        quantity: item.quantity,
        ...(price ? { price: price.amount.toFixed(2) } : {}),
      };
    });

  return {
    email,
    firstname,
    lastname,
    orderRef: order.orderNumber ?? order.externalOrderId,
    // Mongo-backed pages carry the bare Shopify id, CRDB-backed ones the gid.
    platformOrderId:
      parseOrderTrackingSourceOrderId(order.externalOrderId) || undefined,
    mobile: customer?.phone ?? undefined,
    country,
    shippingzipcode: order.shippingAddress?.postalCode ?? undefined,
    language,
    currency: order.currency || undefined,
    amount: hasPricedItem ? amountTotal.toFixed(2) : undefined,
    cartItems: cartItems.length ? cartItems : undefined,
  };
}

function withoutEmptyValues(
  attributes: Record<string, string | undefined>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(attributes).filter(
      (entry): entry is [string, string] => !!entry[1],
    ),
  );
}

/** Rokt Web SDK selectPlacements attributes. */
export function toRoktAttributes(
  attributes: OfferNetworkAttributes,
): Record<string, string> {
  return withoutEmptyValues({
    email: attributes.email,
    firstname: attributes.firstname,
    lastname: attributes.lastname,
    confirmationref: attributes.orderRef,
    mobile: attributes.mobile,
    country: attributes.country,
    shippingzipcode: attributes.shippingzipcode,
    language: attributes.language,
  });
}

/** Falcon orderId disallows special characters (#, @, ., spaces, ...). */
export function sanitizeFalconOrderId(orderId: string): string {
  return orderId.replace(/[^A-Za-z0-9_-]/g, "");
}

/**
 * Falcon Labs embedded SDK attributes (docs.falconlabs.com). Falcon counts
 * orders by `orderId` and expects the platform order id; sending the display
 * order number inflates its order metrics.
 */
export function toFalconAttributes(
  attributes: OfferNetworkAttributes,
): Record<string, string> {
  return withoutEmptyValues({
    orderId: attributes.platformOrderId
      ? sanitizeFalconOrderId(attributes.platformOrderId)
      : undefined,
    email: attributes.email,
    amount: attributes.amount,
    firstname: attributes.firstname,
    lastname: attributes.lastname,
    currency: attributes.currency,
    country: attributes.country,
    language: attributes.language,
    mobile: attributes.mobile,
    cartItems: attributes.cartItems
      ? JSON.stringify(attributes.cartItems)
      : undefined,
  });
}

/**
 * Uptick web SDK config (docs.uptick.com/publishers/web). Uptick names the
 * price fields as display strings; we send the bare decimal and let `currency`
 * carry the unit, since a tracking page has an ISO code and no symbol.
 *
 * `customer_id` (frequency capping) and `payment_method` are deliberately
 * absent: a tracking page has no stable customer id that is not the shopper's
 * email, and sending an email through an id field would widen what Uptick sees.
 */
export function toUptickAttributes(
  attributes: OfferNetworkAttributes,
): Record<string, string> {
  return withoutEmptyValues({
    first_name: attributes.firstname,
    order_id: attributes.orderRef,
    total_price: attributes.amount,
    currency: attributes.currency,
  });
}
